import Phaser from 'phaser';
import {
  SCENE, GAME_WIDTH, GAME_HEIGHT, STREET_SPAWNS, STREET_SPAWN_Y,
  STREET_ASPHALT_BOTTOM_Y, STREET_ASPHALT_TOP_Y, STREET_SIDEWALK_TOP_Y,
} from '../config.js';
import { messageFor } from '../api/errors.js';
import { gameState } from '../state/gameState.js';
import { CarController } from '../ui/CarController.js';
import {
  OUTDOOR_CHARACTER_SCALE, ROAD_WHEEL_Y, ROUTE_FINISH_X, ROUTE_START_OFFSET,
  SEGMENT_WORLD_WIDTH,
} from '../ui/carCatalog.mjs';
import { pxPerSec } from '../ui/carPhysics.mjs';
import { lookahead } from '../ui/cameraLookahead.mjs';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { InstructionStrip } from '../ui/InstructionStrip.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { PedestrianActor } from '../ui/PedestrianActor.js';
import { RouteDirector } from '../ui/RouteDirector.js';
import { createRun } from '../ui/routeRules.mjs';
import { abortToast } from '../ui/schoolCar.mjs';
import { TrafficActor, TrafficLight } from '../ui/TrafficActor.js';
import { buildTasks, JUNCTION_WORLD_WIDTH, junctionKey, layoutSegments, ROUTE_DEFS } from './lessonScript.mjs';
import { SEGMENT_FEATURES } from './segmentFeatures.mjs';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { iconText } from '../ui/iconText.js';

// The training route: the road the practical lessons and the exam are driven on. A route is
// a list of reusable SEGMENTS (`country`, `stop`, `lights`…) laid end to end — the server's
// catalog says which ones, `lessonScript.mjs` lays them out and turns them into the tasks
// the child has to complete, `routeRules.mjs` judges them and this scene builds the world
// they happen in: the road, the traffic, the child on the zebra, the light, the instructor's
// strip along the top.
//
//   scene.start(SCENE.TRAINING_ROUTE, { route: 2, kind: 'lesson', lesson: 2 })
//   scene.start(SCENE.TRAINING_ROUTE, { route: 'exam', kind: 'exam' })
//
// You drive LEFT the whole way, alone: no chat, no presence, no parked cars, no getting out.
// The only ways off are the finish line and „Przerwij". Deliberately NOT restorable: a
// refresh mid-route puts the child back on the street with the session still open, which
// the instructor's menu then aborts.

const CAMERA_TAU = 0.35;           // camera lag, same feel as the street (see OutsideScene)
const CAMERA_TAU_FAST = 0.15;      // …tightening up as the car speeds up
const CAMERA_TAU_FAST_AT = 2000;   // px/s at which the lag has fully tightened

// The drawn road, used for every segment whose art hasn't landed yet. Same bands as the
// street's baked art (see streetSpawns.mjs) so the Yaris — whose wheels sit on ROAD_WHEEL_Y
// either way — stands on the drawn asphalt rather than floating over its edge.
const ROAD_TOP = STREET_ASPHALT_TOP_Y;          // 528
const ROAD_BOTTOM = STREET_ASPHALT_BOTTOM_Y;    // 643
const PAVEMENT_TOP = STREET_SIDEWALK_TOP_Y;     // 491
const SKY = 0x8ecbe8, ASPHALT = 0x5a5a5f, GRASS = 0x6aa84f, VERGE = 0xc9c4ba;

const DEPTH_JUNCTION = 0.5; // junction patches: over the segment art, under the markings
const DEPTH_ROAD = 1;    // painted markings, under everything that moves
const DEPTH_SIGN = 5;    // …signs stand beside the road, behind the cars
const LINE_Y = (ROAD_WHEEL_Y.left + ROAD_WHEEL_Y.right) / 2;   // the centre line between the lanes (fallback art)
// The normalised segment art (compose_segments.py TARGET, ×720/941): asphalt from world y 494,
// centre line at 572. A stop bar is painted across YOUR lane only — the far one, driving left.
const ART_ROAD_TOP = 494;
const ART_LINE_Y = 572;
const SIGN_Y = 460;
const SIGN_BASE_Y = 492;   // where a placed sign post's foot stands: the kerb, in world y
const SIGN_H = 215;        // …and how tall sign + post are drawn (the painted ones were ~190)

const SPAWN_BACK = { spawnX: STREET_SPAWNS.DRIVING_SCHOOL, spawnY: STREET_SPAWN_Y };

// Which segments the fallback art has to explain in words, since it has no signs of its own.
const SEGMENT_NAME_PL = {
  country: 'Droga wiejska', limit50: 'Ograniczenie 50', stop: 'Znak STOP',
  yield: 'Ustąp pierwszeństwa', crossing: 'Przejście dla pieszych', lights: 'Sygnalizacja',
  solid: 'Linia ciągła', school: 'Szkoła — 30', noentry: 'Zakaz wjazdu', parking: 'Parking',
};

export class TrainingRouteScene extends Phaser.Scene {
  constructor() { super(SCENE.TRAINING_ROUTE); }

  init(data) {
    // Set here (not in create) because preload() needs the route to know which segments to fetch.
    this._kind = data?.kind === 'exam' ? 'exam' : 'lesson';
    this._route = this._kind === 'exam' ? 'exam' : (data?.route ?? 1);
  }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx({ route: this._segments() })));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    // The route is outdoors, so everything movable draws at the street's one real-world
    // scale: the child who steps onto the zebra is 1.2 m (120 px) beside a 1.5 m Yaris.
    // Set before anything that reads it (the pedestrian, the hidden driver sprite).
    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    // Scene instances are reused: every field a previous run set has to be re-set here.
    this._lessonN = this._kind === 'lesson' ? (data?.lesson ?? this._route) : null;
    this._leaving = false;
    this._camTarget = null;
    this._camTau = CAMERA_TAU;
    // …including the modal input-lock counter (inputLock.js keeps it on the scene object):
    // nothing is open on the first frame of a route, and a lock left over from the previous
    // run would leave the wheel dead with no way to get it back.
    this._inputLocks = 0;

    const segments = this._segments();
    const { worldWidth, placed } = layoutSegments(segments);
    this.worldWidth = worldWidth;
    this._drawRoute(placed, segments);

    const tasks = buildTasks(segments, SEGMENT_FEATURES, {
      mode: this._kind, lesson: this._lessonN,
    });
    this.run = createRun(tasks, { mode: this._kind });

    // The driver never gets out here, so she exists only because the car controller needs
    // somebody to seat: hidden from the first frame, and never shown again.
    // …and with no wardrobe button: „👗" belongs on a scene you can walk around in, not
    // over the instructor's strip in the middle of a lesson.
    // …and with no walk sheets either: she is never visible here, so there is nothing to
    // animate and no reason to pull the best part of a megabyte into a driving lesson.
    this.natalka = new NatalkaSprite(this, worldWidth - ROUTE_START_OFFSET, 600,
      { worldScale: this.worldScale, noTweakHud: true, walkAnim: false });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.natalka.setVisible(false);

    this.car = new CarController(this, {
      natalka: this.natalka,
      presence: null,          // no room, no peers: the route is yours alone
      onBroadcast: null,       // …so there is nobody to tell where the Yaris is
      parked: false,           // your own street-parked cars are not scenery here
      speedLimit: null,        // a route has no default limit — only the signs on it
      worldWidth,
      onAbortLesson: () => this._abort(),
    });
    this.car.startSchoolCar({
      x: worldWidth - ROUTE_START_OFFSET,
      facing: 'left',
      lane: 'left',            // your own lane, driving left
      lesson: { route: this._route, kind: this._kind, lesson: this._lessonN },
    });
    // Beside the car park the car can pull down a third time, into the lay-by itself.
    const parking = placed.find((seg) => seg.id === 'parking');
    this.car.bayZone = parking ? { x0: parking.x0, x1: parking.x1 } : null;
    this._followWithLag(this.car.sprite ?? this.natalka);

    this.strip = new InstructionStrip(this, {
      portraitKey: 'npc_instructor',
      onAbort: () => this._confirmAbort(),
    });

    this.director = new RouteDirector(this, {
      run: this.run,
      car: this.car,
      strip: this.strip,
      actors: this._buildActors(tasks),
      onFinish: (result) => this._finish(result),
    });

    this._addHeader();
    attachCoordsDebug(this);
    this.events.once('shutdown', () => {
      this.director?.destroy();
      this.director = null;
      this.strip?.destroy();
      this.strip = null;
    });
  }

  /** The segment list for this route: the server's catalog, or the pure fallback. */
  _segments() {
    const driving = gameState.raw?.catalog?.driving;
    if (this._kind === 'exam') {
      return driving?.exam_route?.segments ?? ROUTE_DEFS.exam;
    }
    const route = driving?.routes?.find?.((r) => r.n === this._route);
    return route?.segments ?? ROUTE_DEFS[`lesson${this._route}`] ?? ROUTE_DEFS.lesson1;
  }

  // --- the road ---------------------------------------------------------------------------

  _drawRoute(placed, segments) {
    for (const seg of placed) this._drawSegment(seg);
    this._drawJunctions(placed);
    this._paintMarkings(placed, segments);
  }

  // The blended strip over each boundary between two segments (compose_junctions.py):
  // `placed[i]` is driven first, so the next segment sits to its LEFT and the boundary is
  // `placed[i].x0`. The strip's alpha is feathered, so it just sits on top of both.
  _drawJunctions(placed) {
    for (let i = 0; i + 1 < placed.length; i++) {
      const key = junctionKey(placed[i].id, placed[i + 1].id);
      if (!this.textures.exists(key)) continue;
      this.add.image(placed[i].x0, 0, key).setOrigin(0.5, 0)
        .setDisplaySize(JUNCTION_WORLD_WIDTH, GAME_HEIGHT).setDepth(DEPTH_JUNCTION);
    }
  }

  // One composed 2557×720 background per segment where the art exists (the composites are
  // 3342×941 and are drawn down to the world size), a flat road where it doesn't — the whole
  // route is playable with no image files at all.
  _drawSegment(seg) {
    const key = `bg_seg_${seg.id}`;
    if (this.textures.exists(key)) {
      this.add.image(seg.x0, 0, key).setOrigin(0, 0)
        .setDisplaySize(SEGMENT_WORLD_WIDTH, GAME_HEIGHT).setDepth(0);
      return;
    }
    const W = SEGMENT_WORLD_WIDTH;
    const band = (top, bottom, colour) => this.add
      .rectangle(seg.x0, top, W, bottom - top, colour).setOrigin(0, 0).setDepth(0);
    band(0, PAVEMENT_TOP, SKY);
    band(PAVEMENT_TOP, ROAD_TOP, VERGE);
    band(ROAD_TOP, ROAD_BOTTOM, ASPHALT);
    band(ROAD_BOTTOM, GAME_HEIGHT, GRASS);
    // Low enough to clear the instructor's strip and the route header pinned above it.
    iconText(this, seg.x0 + W / 2, 210, SEGMENT_NAME_PL[seg.id] ?? seg.id, {
      fontFamily: 'system-ui', fontSize: '28px', color: '#ffffff',
      backgroundColor: '#00000055', padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(DEPTH_SIGN);
    // The lane divider: dashed by default (the segment art that exists paints its own).
    for (let x = seg.x0 + 20; x < seg.x1 - 40; x += 90) {
      this.add.rectangle(x, LINE_Y, 46, 5, 0xf2f2f2, 0.85).setOrigin(0, 0.5).setDepth(DEPTH_ROAD);
    }
  }

  // Everything the RULES measure against but no segment art paints: the give-way bar, the
  // zebra, the solid line, the parking bays, the finish. Every case but `stop` and `lights`
  // is guarded by `hasArt` — a segment whose composite has landed paints its own bar/zebra/bays, and
  // drawing ours over it would double every marking the moment the art appears. The finish
  // line is ours either way: no segment art knows where a route ends.
  _paintMarkings(placed, segments) {
    const n = segments.length;
    const worldX = (seg, localX) => n * SEGMENT_WORLD_WIDTH
      - (seg.index + 1) * SEGMENT_WORLD_WIDTH + localX;
    const hasArt = (seg) => this.textures.exists(`bg_seg_${seg.id}`);
    for (const seg of placed) {
      const f = SEGMENT_FEATURES[seg.id] ?? {};
      switch (seg.id) {
        case 'stop':
          // The exceptions to the hasArt guard: bg_seg_stop's and bg_seg_lights' traced art
          // paint the side road but NO stop bar (see segmentFeatures.mjs), so this is the
          // only line the child has to stop at — drawn whether the composite is loaded or not.
          this._bar(worldX(seg, f.stopLine.x));
          this._signSprite(worldX(seg, f.stopSign.x), 'sign_stop', 'STOP');
          break;
        case 'lights':
          this._bar(worldX(seg, f.stopLine.x));
          break;
        case 'yield':
          // The give-way "shark teeth" are ours whether the art is there or not: the painted
          // ones sat across the side road's mouth (the wrong road — you are the one yielding)
          // and were erased from the half.
          this._teeth(worldX(seg, f.yieldLine.x));
          this._signSprite(worldX(seg, f.yieldSign.x), 'sign_yield', 'USTĄP');
          break;
        case 'crossing':
          if (!hasArt(seg)) this._zebra(worldX(seg, f.zebra.x1), worldX(seg, f.zebra.x0));
          this._signSprite(worldX(seg, f.crossingSign.x), 'sign_crossing', 'PRZEJŚCIE');
          break;
        case 'solid':
          if (!hasArt(seg)) this._solidLine(seg.x0, seg.x1);
          break;
        case 'school':
          if (!hasArt(seg)) this._sign(worldX(seg, f.sign30.x), '30');
          break;
        case 'limit50':
          if (!hasArt(seg)) this._sign(worldX(seg, f.sign50.x), '50');
          break;
        case 'noentry':
          if (!hasArt(seg)) this._sign(worldX(seg, f.noEntrySign.x), 'ZAKAZ\nWJAZDU');
          break;
        case 'parking':
          if (!hasArt(seg)) {
            for (const bay of f.bays) this._bay(worldX(seg, bay.x1), worldX(seg, bay.x0));
          }
          break;
        default:
          break;
      }
    }
    if (!segments.includes('parking')) this._finishLine(ROUTE_FINISH_X);
  }

  // A sign the scene places itself (STOP, give way, crossing — the ones that have to stand
  // next to what they announce; their painted twins were erased from the segment art).
  // Falls back to the drawn text sign when the sprite is missing.
  _signSprite(x, key, fallbackText) {
    if (!this.textures.exists(key)) { this._sign(x, fallbackText); return; }
    const img = this.add.image(x, SIGN_BASE_Y, key).setOrigin(0.5, 1).setDepth(DEPTH_SIGN);
    img.setDisplaySize(SIGN_H * (img.width / img.height || 0.4), SIGN_H);
  }

  // The give-way line across YOUR lane: a row of white triangles (P-13 „zęby rekina") with
  // their tips towards the approaching driver, i.e. pointing right, at the judged line.
  _teeth(x) {
    const top = ART_ROAD_TOP + 6;
    const bottom = ART_LINE_Y - 6;
    const n = 4;
    const gap = 4;
    const h = (bottom - top - gap * (n - 1)) / n;
    for (let i = 0; i < n; i++) {
      const y0 = top + i * (h + gap);
      this.add.triangle(0, 0, x - 12, y0, x - 12, y0 + h, x + 12, y0 + h / 2, 0xffffff, 0.95)
        .setOrigin(0, 0).setDepth(DEPTH_ROAD);
    }
  }

  // A line across YOUR lane (the far one) you have to stop before — not the whole road: the
  // near lane is oncoming traffic, which has no reason to stop at this junction.
  _bar(x, colour = 0xffffff, alpha = 0.95) {
    this.add.rectangle(x, ART_ROAD_TOP + 4, 10, ART_LINE_Y - ART_ROAD_TOP - 8, colour, alpha)
      .setOrigin(0.5, 0).setDepth(DEPTH_ROAD);
  }

  _zebra(x1, x0) {
    for (let x = x1 + 12; x < x0; x += 44) {
      this.add.rectangle(x, ROAD_TOP + 6, 24, ROAD_BOTTOM - ROAD_TOP - 12, 0xffffff, 0.9)
        .setOrigin(0, 0).setDepth(DEPTH_ROAD);
    }
  }

  // White, continuous, down the middle of the road: no overtaking for as long as it runs.
  _solidLine(x0, x1) {
    this.add.rectangle(x1, LINE_Y, x0 - x1, 5, 0xffffff, 0.95)
      .setOrigin(0, 0.5).setDepth(DEPTH_ROAD);
  }

  // A parking bay: two lines out of the road into the grass, open towards the driver.
  _bay(x1, x0) {
    for (const x of [x0, x1]) {
      this.add.rectangle(x, ROAD_BOTTOM - 4, 5, 70, 0xffffff, 0.9)
        .setOrigin(0.5, 0).setDepth(DEPTH_ROAD);
    }
    this.add.rectangle((x0 + x1) / 2, ROAD_BOTTOM + 66, x0 - x1, 5, 0xffffff, 0.9)
      .setOrigin(0.5, 0).setDepth(DEPTH_ROAD);
  }

  _finishLine(x) {
    const h = (ROAD_BOTTOM - ROAD_TOP) / 4;
    for (let i = 0; i < 4; i++) {
      this.add.rectangle(x, ROAD_TOP + i * h, 16, h, i % 2 ? 0x111111 : 0xffffff, 0.95)
        .setOrigin(0.5, 0).setDepth(DEPTH_ROAD);
    }
  }

  _sign(x, text) {
    iconText(this, x, SIGN_Y, text, {
      fontFamily: 'system-ui', fontSize: '22px', color: '#b3261e', align: 'center',
      backgroundColor: '#ffffff', padding: { x: 10, y: 6 }, fontStyle: 'bold',
    }).setOrigin(0.5, 1).setDepth(DEPTH_SIGN);
  }

  // --- the traffic ------------------------------------------------------------------------

  // One actor per task that has something to animate, keyed by the task's id — which is how
  // RouteDirector finds it again when the rules wake it.
  _buildActors(tasks) {
    const actors = new Map();
    const models = this._trafficModels();
    let i = 0;
    const nextModel = () => models[i++ % models.length];
    for (const t of tasks) {
      switch (t.kind) {
        case 'stop':
        case 'yield':
          actors.set(t.id, new TrafficActor(this, {
            kind: 'side', model: nextModel(), x: t.meta.sideRoad, junctionX: t.meta.sideRoad,
            worldWidth: this.worldWidth,
          }));
          break;
        case 'crossing':
          // 30 px INSIDE the crossing, not before it: a car that stops correctly puts its
          // bonnet on the zebra's near edge, and a child waiting on the approach side would
          // be standing where that bonnet is.
          actors.set(t.id, new PedestrianActor(this, {
            x: t.meta.zebra.x0 - 30, worldScale: this.worldScale,
          }));
          break;
        case 'light':
          actors.set(t.id, new TrafficLight(this, { x: t.meta.lightPole }));
          break;
        case 'solid':
          actors.set(t.id, new TrafficActor(this, {
            kind: 'parked', model: nextModel(), x: t.meta.parkedCar, worldWidth: this.worldWidth,
          }));
          break;
        case 'oncoming':
          actors.set(t.id, new TrafficActor(this, {
            kind: 'oncoming', model: nextModel(), x: t.meta.trigger, worldWidth: this.worldWidth,
          }));
          break;
        default:
          break;
      }
    }
    return actors;
  }

  // Anything but the school's own Yaris — the child is driving that one.
  _trafficModels() {
    const cars = gameState.raw?.catalog?.cars ?? {};
    const models = Object.keys(cars).filter((m) => m !== 'yaris_l');
    return models.length ? models : ['panda', 'bclass', 'q5'];
  }

  // Which route this is and (in a lesson) which lesson. Pinned to the camera, and kept
  // clear of the instructor's strip along the very top of the screen.
  _addHeader() {
    const title = this._kind === 'exam'
      ? 'Egzamin praktyczny'
      : `Lekcja ${this._lessonN}`;
    iconText(this, 16, 84, title, {
      fontFamily: 'system-ui', fontSize: '18px', color: '#ffffff',
      backgroundColor: '#000000aa', padding: { x: 12, y: 6 },
    }).setScrollFactor(0).setDepth(900);
  }

  // --- the end of the route -------------------------------------------------------------

  /**
   * The route is over (the director already froze the wheel and waited a second). The server
   * scores the run — it, not us, decides how many faults there were and whether an exam was
   * passed — and the street shows the result. A refusal (most often DRIVING_TOO_FAST, the
   * "nobody drives a whole route in 8 s" guard) is said out loud and the run is thrown away:
   * the session stays open server-side, where the instructor's menu aborts it next time.
   */
  async _finish({ run }) {
    if (this._leaving) return;
    this._leaving = true;
    const rows = run.tasks.map(({ icon, title, severity, status }) => ({
      icon, title, severity, status,
    }));
    let practical;
    try {
      const out = this._kind === 'exam'
        ? await gameState.drivingPracticalFinish(run.results())
        : await gameState.drivingLessonFinish(run.results());
      practical = out?.practical ?? {};
    } catch (e) {
      this.scene.start(SCENE.OUTSIDE, { ...SPAWN_BACK, toast: messageFor(e) });
      return;
    }
    this.scene.start(SCENE.OUTSIDE, {
      ...SPAWN_BACK,
      practicalResult: {
        kind: this._kind,
        n: this._lessonN,
        passed: !!practical.passed,   // the server's verdict for lessons (every task ok) and the exam alike
        minor: practical.minor ?? 0,
        major: practical.major ?? 0,
        tasks: rows,
      },
    });
  }

  // The strip's ✖ asks the same question the car's menu row does, and means the same thing.
  _confirmAbort() {
    this.car?.confirmAbortLesson?.();
  }

  // „Przerwij lekcję/egzamin": cancel the session and go back to the pavement in front of
  // the school, exactly as the street's own abort does.
  async _abort() {
    if (this._leaving) return;
    this._leaving = true;
    this.car.lockInput();
    const kind = this._kind;
    let msg = abortToast(kind);
    try {
      if (kind === 'exam') await gameState.drivingPracticalAbort();
      else await gameState.drivingLessonAbort();
    } catch (e) {
      // The session is gone one way or another (expired, aborted in another tab): still
      // get off the route — being stuck on it with no lesson is worse.
      msg = messageFor(e);
    }
    this.scene.start(SCENE.OUTSIDE, { ...SPAWN_BACK, toast: msg });
  }

  // --- camera ---------------------------------------------------------------------------

  // Same hand-rolled soft follow as the street: no camera bounds (they would fight the
  // top-left retina zoom), the scroll is clamped in world units instead.
  _followWithLag(target) {
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.roundPixels = false;   // sub-pixel scroll: whole-pixel steps read as stutter
    const snap = !this._camTarget;
    this._camTarget = target;
    if (snap) cam.scrollX = this._desiredScrollX();
  }

  // The camera centres on the car at rest and slides ahead of it as it speeds up (see
  // cameraLookahead.mjs), so at speed the child sees the road they are driving INTO.
  _desiredScrollX() {
    const ahead = this.car?.isDriving ? lookahead(this.car.drive) : 0;
    const x = (this._camTarget?.x ?? 0) + ahead - GAME_WIDTH / 2;
    return Math.max(0, Math.min(this.worldWidth - GAME_WIDTH, x));
  }

  _carCamTau() {
    const t = Math.min(1, pxPerSec(this.car?.drive?.v ?? 0) / CAMERA_TAU_FAST_AT);
    return CAMERA_TAU + (CAMERA_TAU_FAST - CAMERA_TAU) * t;
  }

  update(_t, dt) {
    if (this._leaving) return;
    this.car.update(dt);
    // AFTER the car's frame: `laneChangedThisFrame` belongs to the update that just ran.
    this.director?.update(dt);
    this._camTau = this.car.isDriving ? this._carCamTau() : CAMERA_TAU;
    if (this._camTarget) {
      const cam = this.cameras.main;
      const k = 1 - Math.exp(-(Math.min(dt, 100) / 1000) / (this._camTau || CAMERA_TAU));
      cam.scrollX += (this._desiredScrollX() - cam.scrollX) * k;
    }
    this.car.syncAction();
  }
}
